import { useState } from 'react';
import { ImagePlus, X, Images } from 'lucide-react';
import AssetPicker, { type AssetPickResult } from '@/components/admin/AssetPicker';
import { ASSET_SPEC_BY_KEY, assetPrompt } from '@/config/asset-specs';

/**
 * A labelled image field backed by the asset-spec registry.
 *
 * Drop-in replacement for the bare "Image URL" text inputs scattered through
 * the admin (categories, the default OG image, and every homepage section that
 * falls through to a raw-JSON textarea). Those fields expected a person to know
 * the right dimensions, produce a file at that size somewhere else, host it,
 * and paste a URL — which is why so many of them are empty on live sites.
 *
 * Pass `specKey` and everything else follows: the size hint, the crop target
 * handed to the uploader and the stock importer, and the AI prompt seed all
 * come from one registry entry, so they cannot drift apart.
 */
export default function AssetField({
  specKey,
  value,
  onChange,
  label,
  helperText,
  folder = 'cms',
}: {
  /** A key from `config/asset-specs.ts`, e.g. 'category.image_url'. */
  specKey: string;
  value: string;
  onChange: (url: string) => void;
  /** Overrides the registry label. */
  label?: string;
  helperText?: string;
  folder?: 'homepage' | 'blog' | 'cms';
}) {
  const [open, setOpen] = useState(false);
  const spec = ASSET_SPEC_BY_KEY[specKey];
  const fieldLabel = label ?? spec?.label ?? 'Image';

  function handlePick(result: AssetPickResult) {
    onChange(result.url);
  }

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="block text-sm font-medium text-gray-700">{fieldLabel}</span>
        {spec && (
          <span className="text-xs text-gray-500">
            {spec.width} × {spec.height} · {spec.format.toUpperCase()}
          </span>
        )}
      </div>

      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt=""
            className="h-28 w-auto max-w-full rounded-lg border border-gray-200 bg-gray-100 object-contain"
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -right-2 -top-2 rounded-full bg-gray-900 p-1 text-white shadow-sm hover:bg-gray-700"
            aria-label={`Remove ${fieldLabel}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-28 w-full max-w-xs flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-300 text-sm text-gray-500 transition-colors hover:border-sage-400 hover:text-sage-700"
        >
          <ImagePlus className="h-6 w-6" aria-hidden="true" />
          Choose an image
        </button>
      )}

      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm text-sage-700 hover:underline"
        >
          <Images className="h-3.5 w-3.5" aria-hidden="true" />
          {value ? 'Replace' : 'Library, upload, stock or AI'}
        </button>
      </div>

      {(helperText || spec?.notes) && (
        <p className="mt-1 text-xs text-gray-500">{helperText ?? spec?.notes}</p>
      )}

      {open && (
        <AssetPicker
          isOpen
          onClose={() => setOpen(false)}
          onPick={handlePick}
          targetWidth={spec?.width}
          targetHeight={spec?.height}
          slotLabel={fieldLabel}
          promptHint={assetPrompt(specKey)}
          folder={folder}
        />
      )}
    </div>
  );
}
